import type { TFunction } from "i18next"

import type { Automation } from "../types/automation.types"
import { durationVars } from "./localized-summary"

// Display text for the overview list. Stored values stay untouched; these
// only describe them.

export type ListStatus = "active" | "paused" | "draft"

// The backend keeps `enabled_at` when an automation is turned off, so a
// disabled record that was never enabled is still a draft.
export const listStatus = (automation: Automation): ListStatus =>
  automation.is_enabled ? "active" : automation.enabled_at ? "paused" : "draft"

export const describeSends = (t: TFunction, { send_count }: Automation) =>
  send_count === 1
    ? t("settingsAutomation.table.sendsOnce")
    : t("settingsAutomation.table.sends", { count: send_count })

export const describeTrigger = (
  t: TFunction,
  { category, lead_status_label, lead_status_available }: Automation
) => {
  if (category === "NURTURING") return t("settingsAutomation.table.inNurturing")
  return lead_status_available
    ? lead_status_label
    : t("settingsAutomation.table.unavailableState", { state: lead_status_label })
}

export const describeCategory = (t: TFunction, { category }: Automation) =>
  t(`settingsAutomation.category.${category ?? "LEAD"}`)

export const describeTiming = (t: TFunction, automation: Automation) =>
  t(
    "settingsAutomation.table.after",
    durationVars(t, "duration", automation.initial_delay_value, automation.initial_delay_unit)
  )

export const describeRepeat = (t: TFunction, automation: Automation) =>
  automation.send_count > 1
    ? t(
        "settingsAutomation.table.thenEvery",
        durationVars(t, "duration", automation.cadence_value ?? 1, automation.cadence_unit ?? "days")
      )
    : null

export const describeMessage = (t: TFunction, { template_name }: Automation) =>
  template_name ?? t("settingsAutomation.table.customMessage")

export const matchesQuery = (t: TFunction, automation: Automation, query: string) => {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return [
    automation.name,
    describeTrigger(t, automation),
    describeMessage(t, automation),
    automation.sender_name,
    automation.sender_email
  ].some((text) => text?.toLowerCase().includes(needle))
}

// The overview's sort, remembered per user. Client-side: the whole list is
// loaded before it is shown.
export type OverviewSortColumn = "category" | "status" | "updated"
export interface OverviewSort {
  column: OverviewSortColumn
  order: "ascend" | "descend"
}

export const DEFAULT_OVERVIEW_SORT: OverviewSort = { column: "updated", order: "descend" }

const SORT_COLUMNS: OverviewSortColumn[] = ["category", "status", "updated"]

// A stored value is whatever the preference bag held; anything else reads as the default.
export const readOverviewSort = (stored: unknown): OverviewSort => {
  const candidate = stored as Partial<OverviewSort> | null
  return candidate &&
    SORT_COLUMNS.includes(candidate.column as OverviewSortColumn) &&
    (candidate.order === "ascend" || candidate.order === "descend")
    ? { column: candidate.column as OverviewSortColumn, order: candidate.order }
    : DEFAULT_OVERVIEW_SORT
}

const STATUS_RANK: Record<ListStatus, number> = { active: 0, paused: 1, draft: 2 }

export const compareBy: Record<OverviewSortColumn, (a: Automation, b: Automation) => number> = {
  category: (a, b) => (a.category ?? "LEAD").localeCompare(b.category ?? "LEAD"),
  status: (a, b) => STATUS_RANK[listStatus(a)] - STATUS_RANK[listStatus(b)],
  updated: (a, b) => Date.parse(a.updated) - Date.parse(b.updated)
}
